import { authedApiFetch } from '../../../utils/Api';

/**
 * Service for handling prompt-related API calls
 */
const promptService = {
	/**
	 * Fetch all prompts
	 * @param {string} tenantId - The tenant ID
	 * @param {Object} options - Additional options
	 * @param {boolean} options.includeTenantDefault - Whether to include tenant default version info
	 * @returns {Promise<Array>} - Prompts data
	 */
	getPrompts: async (tenantId, options = {}) => {
		const { includeTenantDefault = false } = options;

		let endpoint = `/tenants/${tenantId}/prompts`;
		if (includeTenantDefault) {
			endpoint += `?include_tenant_default=true`;
		}

		const response = await authedApiFetch({
			endpoint,
			method: 'GET',
		});
		return response;
	},

	/**
	 * Get a specific prompt
	 * @param {string} tenantId - The tenant ID
	 * @param {string} promptId - The prompt ID
	 * @returns {Promise<Object>} - Prompt data
	 */
	getPrompt: async (tenantId, promptId, options = {}) => {
		const { includeTenantDefault = false } = options;

		let endpoint = `/tenants/${tenantId}/prompts/${promptId}`;
		if (includeTenantDefault) {
			endpoint += `?include_tenant_default=true`;
		}

		const response = await authedApiFetch({
			endpoint,
			method: 'GET',
		});
		return response;
	},

	/**
	 * Create a new prompt
	 * @param {string} tenantId - The tenant ID
	 * @param {Object} promptData - The prompt data
	 * @returns {Promise<Object>} - Created prompt
	 */
	createPrompt: async (tenantId, promptData) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/prompts`,
			method: 'POST',
			payload: { prompt: promptData },
		});
		return response;
	},

	/**
	 * Update an existing prompt
	 * @param {string} tenantId - The tenant ID
	 * @param {string} promptId - The prompt ID
	 * @param {Object} promptData - The prompt data
	 * @returns {Promise<Object>} - Updated prompt
	 */
	updatePrompt: async (tenantId, promptId, promptData) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/prompts/${promptId}`,
			method: 'PATCH',
			payload: { prompt: promptData },
		});
		return response;
	},

	/**
	 * Delete a prompt
	 * @param {string} tenantId - The tenant ID
	 * @param {string} promptId - The prompt ID
	 * @returns {Promise<void>}
	 */
	deletePrompt: async (tenantId, promptId) => {
		await authedApiFetch({
			endpoint: `/tenants/${tenantId}/prompts/${promptId}`,
			method: 'DELETE',
		});
	},

	/**
	 * Toggle a prompt's active status
	 * @param {string} tenantId - The tenant ID
	 * @param {string} promptId - The prompt ID
	 * @param {boolean} active - The active status
	 * @returns {Promise<Object>} - Updated prompt
	 */
	toggleActive: async (tenantId, promptId, active) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/prompts/${promptId}`,
			method: 'PATCH',
			payload: { prompt: { active } },
		});
		return response;
	},

	/**
	 * Get all versions of a prompt
	 * @param {string} tenantId - The tenant ID
	 * @param {string} promptId - The prompt ID
	 * @returns {Promise<Array>} - Versions data
	 */
	getVersions: async (tenantId, promptId) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/prompts/${promptId}/versions`,
			method: 'GET',
		});
		return response;
	},

	/**
	 * Get a specific version of a prompt
	 * @param {string} tenantId - The tenant ID
	 * @param {string} promptId - The prompt ID
	 * @param {number} versionNumber - The version number
	 * @returns {Promise<Object>} - Version data
	 */
	getVersion: async (tenantId, promptId, versionNumber) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/prompts/${promptId}/versions/${versionNumber}`,
			method: 'GET',
		});
		return response;
	},

	/**
	 * Set a version as the active version
	 * @param {string} tenantId - The tenant ID
	 * @param {string} promptId - The prompt ID
	 * @param {number} versionNumber - The version number
	 * @returns {Promise<Object>} - Updated prompt
	 */
	setActiveVersion: async (tenantId, promptId, versionNumber) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/prompts/${promptId}/versions/${versionNumber}/set_active`,
			method: 'PATCH',
		});
		return response;
	},

	/**
	 * Get prompt types
	 * @param {string} tenantId - The tenant ID
	 * @returns {Promise<Array>} - Prompt types
	 */
	getPromptTypes: async (tenantId) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/config_lists?list_type=prompt_types`,
			method: 'GET',
		});

		// The config_list_items are nested in the first response item
		const configListItems = response[0]?.config_list_items || [];

		// Transform the config list items into prompt types
		return configListItems.map((item) => ({
			id: item.id,
			value: item.value,
			name: item.display_name,
		}));
	},
	/**
	 * Set a version as the tenant default
	 * @param {string} tenantId - The tenant ID
	 * @param {string} promptId - The prompt ID
	 * @param {number} promptVersionId - The prompt version ID (optional, if not provided, clears the tenant default)
	 * @returns {Promise<Object>} - Response with tenant default info
	 */
	setTenantDefaultVersion: async (
		tenantId,
		promptId,
		promptVersionId = null
	) => {
		const payload = promptVersionId
			? { prompt_version_id: promptVersionId }
			: {};

		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/prompts/${promptId}/set_tenant_default_version`,
			method: 'POST',
			payload,
		});
		return response;
	},

	/**
	 * Fork a prompt (copy system prompt to tenant)
	 * @param {string} tenantId - The tenant ID
	 * @param {string} promptId - The prompt ID to fork
	 * @param {number} versionNumber - Specific version to fork
	 * @returns {Promise<Object>} - Forked prompt data
	 */
	forkPrompt: async (tenantId, promptId, versionNumber = null) => {
		const payload = { version_number: versionNumber };

		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/prompts/${promptId}/fork`,
			method: 'POST',
			payload,
		});
		return response;
	},
};

export default promptService;
